import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { get } from "@rails/request.js"
import { showConfirmModal, showAlertModal } from "../lib/custom_confirm"

export const SEARCH_DEBOUNCE_MS = 250
const MIN_QUERY_LENGTH = 2

// Connects to data-controller="coupon-email-agent" (on the coupon form,
// alongside coupon-code-form).
//
// "Email Agent" flow for the New Coupon modal: validate the form, open the
// agent picker (stacked above the coupon modal), confirm via showConfirmModal,
// then stamp the chosen user id into the hidden email_agent_user_id field and
// submit the form — the create action queues the coupon-details email only
// after the coupon saves, so the emailed code is guaranteed to exist.
//
// Audience targeting (#13071): when the form's IMO/Agency Filters name an
// agent, that agent IS the recipient — the picker is skipped and the confirm
// names them (changing the recipient means changing the coupon's Agent, not
// this email). When only an IMO/agency is chosen, the picker searches inside
// that org. The server re-resolves the recipient either way
// (CouponCodes::EmailAgentRecipient), so a stamped id is a request, not a grant.
//
// The picker is a native <input> + <datalist> autocomplete rather than a
// Select2 widget: Select2's selection box does not open on tap on iOS inside
// this modal (the same root cause that killed the product filters), and an
// AJAX-backed Select2 cannot fall back to a plain <select>. A native input is
// always tappable and the <datalist> popup is rendered by the browser, so the
// flow works on every device with no custom dropdown markup.
export default class extends Controller {
  static targets = ["modal", "input", "options", "userIdField"]
  static values = { searchUrl: String }

  connect() {
    // label ("Name (email)") -> { id, text }, refreshed on every search so the
    // chosen id is always resolved from a real result, never typed free-hand.
    this.agentsByLabel = new Map()
  }

  disconnect() {
    this.cancelPendingSearch()
    this.bootstrapModal?.dispose()
    this.bootstrapModal = null
  }

  // Cancel a queued debounce and abort any in-flight request so neither can land
  // after the picker is torn down (disconnect) or re-opened (reset).
  cancelPendingSearch() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
    this.searchAbortController?.abort()
    this.searchAbortController = null
  }

  async open(event) {
    event.preventDefault()
    // The email describes the coupon about to be created — require a complete
    // form before offering to send it.
    if (!this.element.checkValidity()) {
      this.element.reportValidity()
      return
    }

    // An assigned agent is the only possible recipient: confirm them directly.
    const assigned = this.assignedAudienceAgent()
    if (assigned) {
      await this.confirmAndSubmit(assigned, { assigned: true })
      return
    }

    this.reset()
    // No backdrop of its own. This picker is rendered INSIDE the coupon
    // FormModal's Turbo frame, i.e. inside a `position: fixed; z-index: 1055`
    // stacking context — no z-index on the picker can reach a body-level
    // backdrop. The former "raise the backdrop to 1059" bump therefore painted
    // Bootstrap's dim layer OVER the picker's own controls (found by the UI
    // certification's fixed-chrome rule, PR 17 / #11753). The host FormModal
    // already dims the page; the picker dismisses via Cancel, ×, or Escape.
    this.bootstrapModal ||= Modal.getOrCreateInstance(this.modalTarget, { backdrop: false })
    this.bootstrapModal.show()
    // Raise this picker above the coupon FormModal's body (within that modal's
    // stacking context). Formerly an inline `style: 'z-index: 1060'` on the
    // .modal; moved here when the markup migrated to the gl_modal helper, which
    // has no style arg. Then focus the input so the keyboard/suggestions are one
    // tap away.
    this.modalTarget.style.zIndex = "var(--gl-zindex-1060)"
    requestAnimationFrame(() => {
      if (this.hasInputTarget) this.inputTarget.focus()
    })
  }

  // Debounced remote search. Typing after a pick clears the resolved selection
  // implicitly (selectedAgent() re-reads the current input text each time).
  search() {
    if (this.searchTimer) clearTimeout(this.searchTimer)

    const term = this.inputTarget.value.trim()
    // Selecting a <datalist> option fires an `input` event whose value is the
    // full "Name (email)" label. That already resolves to a fetched agent, so
    // skip the re-search — otherwise performSearch would query the server with
    // the label, get no match, clear agentsByLabel, and make a valid pick read
    // as "no agent selected".
    if (this.agentsByLabel.has(term)) return

    if (term.length < MIN_QUERY_LENGTH) {
      this.renderOptions([])
      return
    }

    this.searchTimer = setTimeout(() => this.performSearch(term), SEARCH_DEBOUNCE_MS)
  }

  async performSearch(term) {
    // Cancel any in-flight search so a slow earlier response can't resolve after
    // a newer one and overwrite the option map with stale results (mobile RTT can
    // exceed the debounce). The signal aborts the real request; the post-await
    // `aborted` checks also stop a superseded response that already came back.
    this.searchAbortController?.abort()
    const controller = new AbortController()
    this.searchAbortController = controller

    try {
      const response = await get(this.searchUrlValue, {
        query: this.searchQuery(term),
        responseKind: "json",
        signal: controller.signal
      })
      if (controller.signal.aborted) return
      if (!response.ok) {
        this.renderOptions([])
        return
      }
      const data = await response.json
      if (controller.signal.aborted) return
      this.renderOptions(Array.isArray(data) ? data : [])
    } catch (e) {
      // A superseding search aborted this one — leave the newer results in place.
      if (e?.name === "AbortError" || controller.signal.aborted) return
      // Network/parse failure: clear suggestions rather than leave stale ones.
      this.renderOptions([])
    }
  }

  // The endpoint returns [{ id, text: "Name (email)" }]. Mirror each into a
  // <datalist> option (keyed by the unique label) and remember the id.
  renderOptions(agents) {
    // Bail before mutating any state when the datalist is gone (e.g. mid-teardown)
    // so the map and the DOM can't diverge.
    if (!this.hasOptionsTarget) return

    this.agentsByLabel.clear()
    this.optionsTarget.innerHTML = ""
    agents.forEach((agent) => {
      if (!agent || agent.id == null || !agent.text) return
      this.agentsByLabel.set(agent.text, { id: String(agent.id), text: agent.text })
      const option = document.createElement("option")
      option.value = agent.text
      this.optionsTarget.appendChild(option)
    })
    // Toggle the combobox expanded state with the suggestion count so screen
    // readers announce the popup only when there are options.
    this.setExpanded(this.agentsByLabel.size > 0)
  }

  setExpanded(expanded) {
    if (this.hasInputTarget) this.inputTarget.setAttribute("aria-expanded", expanded ? "true" : "false")
  }

  async send(event) {
    event.preventDefault()
    const agent = this.selectedAgent()
    if (!agent) {
      await showAlertModal("Please select an agent first", { variant: "warning", title: "No agent selected" })
      return
    }

    await this.confirmAndSubmit(agent)
  }

  // The explicit confirmation both paths share. The assigned-agent copy names
  // the saved recipient so the admin sees exactly who receives the code.
  async confirmAndSubmit(agent, { assigned = false } = {}) {
    const message = assigned
      ? `This coupon is assigned to ${agent.text}. Send the coupon details to them?`
      : `Are you sure you want to send an email to ${agent.text} with the coupon details?`
    const confirmed = await showConfirmModal(
      message,
      { title: "Send Coupon Email", variant: "primary", confirmText: "Yes, Send Email" }
    )
    if (!confirmed) return

    this.userIdFieldTarget.value = agent.id
    this.bootstrapModal?.hide()
    this.element.requestSubmit()
  }

  // ── Audience (IMO/Agency Filters card on the same form) ──────────────────
  //
  // Read through the DOM rather than a Stimulus outlet: the card is optional
  // markup on the form, and an absent card simply means "unrestricted".

  audienceField(name) {
    return this.element.querySelector(`[name="coupon_code[${name}]"]`)
  }

  // { id, text } of the agent picked in the IMO/Agency Filters card, or null.
  assignedAudienceAgent() {
    const idField = this.audienceField("audience_agent_id")
    const id = idField?.value?.trim()
    if (!id) return null
    const label = this.element.querySelector('[data-coupon-audience-target="agentInput"]')?.value?.trim()
    return { id, text: label || `agent #${id}` }
  }

  // The search query, narrowed to the chosen IMO/agency so the picker never
  // offers an agent the server would refuse.
  searchQuery(term) {
    const query = { q: term }
    const imo = this.audienceField("audience_imo_id")
    const agency = this.audienceField("audience_agency_id")
    if (imo?.value) query.imo_organization_id = imo.value
    if (agency?.value && !agency.disabled) query.agency_organization_id = agency.value
    return query
  }

  // Resolve the id from the current input text against the last result set. A
  // value the user typed that doesn't match a real agent yields null, so send()
  // shows the "select an agent" prompt instead of submitting a bad id.
  selectedAgent() {
    return this.agentsByLabel.get(this.inputTarget.value.trim()) || null
  }

  reset() {
    // Cancel any queued/in-flight search so it can't repopulate the datalist
    // after the picker is reopened with an empty input.
    this.cancelPendingSearch()
    this.agentsByLabel.clear()
    if (this.hasInputTarget) this.inputTarget.value = ""
    if (this.hasOptionsTarget) this.optionsTarget.innerHTML = ""
    this.setExpanded(false)
  }
}
